"use client";

import React, { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw, ScrollText } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  getActivityLogs,
  loadActivityLogsFromServer,
  subscribeActivityLogs,
  ActivityLogEntry,
} from "@/lib/client-activity-log";

interface ActivityLogDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ActivityLogDialog({
  open,
  onOpenChange,
}: ActivityLogDialogProps) {
  const { translate } = useLanguage();
  const [entries, setEntries] = useState<ActivityLogEntry[]>([]);
  const [refreshing, setRefreshing] = useState(false);

  const refresh = useCallback(() => {
    setEntries(getActivityLogs());
  }, []);

  // React to local log changes
  useEffect(() => {
    refresh();
    return subscribeActivityLogs(refresh);
  }, [refresh]);

  // On open, pull the user's logs from the database
  useEffect(() => {
    if (!open) return;
    setRefreshing(true);
    void loadActivityLogsFromServer()
      .catch(() => undefined)
      .finally(() => {
        setRefreshing(false);
        refresh();
      });
  }, [open, refresh]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await loadActivityLogsFromServer();
    } catch {
      // Keep local logs on failure
    } finally {
      setRefreshing(false);
      refresh();
    }
  };

  const formatTime = (iso: string): string => {
    try {
      const date = new Date(iso);
      if (Number.isNaN(date.getTime())) return iso;
      return date.toLocaleString();
    } catch {
      return iso;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScrollText className="h-5 w-5" />
            {translate("activityLog")}
          </DialogTitle>
          <DialogDescription>
            {translate("activityLogDescription")}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            {translate("activityLogCount").replace(
              "{count}",
              String(entries.length)
            )}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            {refreshing ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            {translate("refresh")}
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto pr-1">
          {entries.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">
              {translate("activityLogEmpty")}
            </p>
          ) : (
            <ul className="space-y-2">
              {entries.map((entry) => (
                <li key={entry.id} className="rounded-lg border p-3 text-sm">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-xs font-semibold text-primary">
                      {entry.action}
                    </span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {formatTime(entry.timestamp)}
                    </span>
                  </div>
                  <p className="mt-1 break-words text-foreground">
                    {entry.detail}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
